import type { AgentState } from '@tabel/shared/connector/protocol';
import type { AgentId } from '@tabel/shared/connector/versions';

/*
 * Тексты страницы «Мои ИИ-клиенты» (005, T059, T147; FR-060; spec.md, US7). Язык — ASD-STE100: короткие предложения,
 * условие первым, одно слово — одно значение, без латиницы. Проверяет `texts.test.tsx` (`@tabel/shared/plain-language`).
 * Название клиента, хост его документа и тексты прав приходят с сервера — здесь их нет.
 *
 * Тексты 006 (компьютеры и агенты; specs/006-agent-client/contracts/ui.md §1, §4, «Слова интерфейса» spec.md) — ниже,
 * в объектах `PC_*`: латиница в них — только названия «Claude», «Codex», «pi», «ChatGPT», «API», «macOS», «Linux»,
 * имя компьютера и команды для терминала. «Команда» в них — строка для терминала (FR-010), а не люди.
 */

/** Заголовки — до 4 слов. */
export const TITLE = {
  page: 'Мои ИИ-клиенты',
  // 006 (contracts/ui.md §1): карточка 005 называется так же, как в мокапе — рядом встали «Компьютеры» и «Агенты».
  list: 'Подключения агентов к табелю',
  empty: 'Подключений нет',
  howTo: 'Как подключить ИИ-агента',
  past: 'Прошлые подключения',
  loadError: 'Список не загрузился',
  forbidden: 'Подключения недоступны',
  samples: 'Мои выборки',
  samplesEmpty: 'Выборок нет',
  samplesError: 'Выборки не загрузились',
} as const;

/** Подписи полей, кнопок и состояний — до 6 слов. */
export const LABEL = {
  clientDoc: 'Документ клиента:',
  scopes: 'Права',
  connectedAt: 'Подключено',
  lastUsedAt: 'Последний вызов',
  state: 'Состояние',
  noCalls: 'Вызовов не было',
  revoke: 'Отозвать',
  revokeConfirm: 'Отозвать подключение',
  revoking: 'Отзыв…',
  cancel: 'Отмена',
  retry: 'Повторить',
  loading: 'Загрузка…',
  errorCode: 'Код ошибки:',
  samplesLoading: 'Загрузка выборок…',
  client: 'Клиент',
  condition: 'Условие',
  days: 'Дней',
  fromUi: 'Интерфейс табеля',
} as const;

/** Состояние подключения: подпись метки. */
export const STATE_LABEL = {
  active: 'Действует',
  revoked: 'Отозвано',
  expired: 'Срок истёк',
} as const;

/** Предложения страницы. */
export const T = {
  intro: 'Здесь показаны ИИ-агенты, которые вы подключили к табелю. Агент работает только с вашими правами.',
  emptyIntro: 'Вы ещё не подключили ИИ-агента к табелю.',
  confirm:
    'Агент больше не сможет работать с табелем от вашего имени. Чтобы подключить агента снова, начните вход в агенте заново.',
  noActive:
    'Сейчас ни одно подключение не действует. Чтобы агент снова работал с табелем, подключите его заново.',
  past: 'Эти подключения отозваны, или их срок истёк. Агент не может работать по ним.',
  forbidden: 'В этой роли нельзя подключать ИИ-агентов.',
  forbiddenFix: 'Если у вас есть другая роль, выберите её.',
  revokeFailed: 'Подключение не отозвано',
  samplesIntro:
    'Здесь показаны выборки дней, которые получил ваш ИИ-агент. Чьи это дни, видит только администратор системы.',
  samplesEmpty: 'Здесь появятся выборки дней, которые получит ваш ИИ-агент.',
  samplesForbidden: 'В этой роли выборок дней нет.',
} as const;

/** Как подключить ИИ-агента: по порядку. */
export const HOW_TO = [
  { title: 'Получите право «ИИ-агент»', text: 'Право выдаёт администратор системы.' },
  {
    title: 'Начните вход в агенте',
    text: 'В ИИ-агенте на своём компьютере начните вход в табель. Агент откроет страницу входа в браузере.',
  },
  {
    title: 'Разрешите доступ',
    text: 'Войдите своим логином и паролем и нажмите «Разрешить». Подключение появится на этой странице.',
  },
] as const;

/** Тексты с подстановкой: `name` — название клиента с сервера, `when` — дата и время подключения. */
export const say = {
  /** Вопрос окна подтверждения. */
  confirmTitle: (name: string) => `Отозвать подключение «${name}»?`,
  /**
   * Название кнопки «Отозвать» для чтения с экрана: с названием клиента. Если подключений одного клиента несколько —
   * ещё и время подключения, чтобы названия кнопок не совпадали.
   */
  revokeName: (name: string, when?: string) =>
    when ? `Отозвать подключение «${name}» от ${when}` : `Отозвать подключение «${name}»`,
  revoked: (name: string) => `Подключение «${name}» отозвано`,
  /** `404` на отзыв: подключение отозвали раньше (на другой вкладке, сменой пароля, снятием права). */
  alreadyRevoked: (name: string) => `Подключение «${name}» уже отозвано`,
};

/* ---------- 006: компьютеры и агенты (contracts/ui.md §1, §4; FR-001–FR-010, FR-060) ---------- */

/** Заголовки — до 4 слов. */
export const PC_TITLE = {
  computers: 'Компьютеры',
  disabledComputers: 'Отключённые компьютеры',
  empty: 'Компьютеров нет',
  loadError: 'Компьютеры не загрузились',
  connect: 'Подключить компьютер',
  keyError: 'Ключ не получен',
} as const;

/** Подписи кнопок, бейджей и полей — до 6 слов. */
export const PC_LABEL = {
  connect: 'Подключить компьютер',
  howToRemove: 'Как удалить с компьютера',
  showAgents: 'Показать агентов',
  disable: 'Отключить',
  disableConfirm: 'Отключить компьютер',
  disabling: 'Отключение…',
  cancel: 'Отмена',
  goToAgents: 'Перейти к агентам',
  copy: 'Скопировать',
  copied: 'Скопировано',
  newKey: 'Получить новый ключ',
  issuing: 'Получение ключа…',
  retry: 'Повторить',
  system: 'Система компьютера',
  command: 'Команда для терминала',
  insideCommand: 'Команда внутри агента',
  online: 'в сети',
  disabled: 'отключён',
  installing: 'Установка',
  installRunning: 'Идёт установка',
  otherInstall: 'Идёт другая установка',
  installOutput: 'Вывод установки',
  checking: 'Проверка…',
  loading: 'Загрузка компьютеров…',
  noAgents: 'агенты не установлены',
  subscriptionBlocked: 'Нужен вход ключом API',
} as const;

/** Система компьютера: подпись переключателя и строки компьютера. */
export const OS_LABEL: Readonly<Record<'macos' | 'linux', string>> = { macos: 'macOS', linux: 'Linux' };

/** Программа агента в строке версии (contracts/ui.md §4): «Claude Code 2.1.302 · вход: подписка Claude». */
export const PROGRAM: Readonly<Record<AgentId, string>> = { claude: 'Claude Code', codex: 'Codex', pi: 'pi' };

/** Состояние агента (FR-008): бейдж. */
export const AGENT_STATE_LABEL: Readonly<Record<AgentState, string>> = {
  ready: 'Готов',
  not_installed: 'Не установлен',
  outdated: 'Нужна новая версия',
  logged_out: 'Вход не выполнен',
  login_expired: 'Вход истёк',
  not_linked: 'Не подключён к табелю',
  unknown: 'Неизвестно',
};

/** Способ входа в агента (US2-1): «вход: подписка Claude». Подписка у каждого агента своя. */
export const LOGIN_LABEL = {
  subscription: { claude: 'подписка Claude', codex: 'подписка ChatGPT', pi: 'подписка' },
  api_key: 'ключ API',
  chatgpt_via_pi: 'ChatGPT через pi',
  other: 'другой способ',
} as const;

/** Предложения карточек и окна. */
export const PC_T = {
  intro:
    'Здесь показаны ваши компьютеры с коннектором и ИИ-агенты, которые вы подключили к табелю. Агент работает только с вашими правами.',
  computers:
    'На вашем компьютере работает коннектор — программа табеля. Он запускает агентов, сам подключается к табелю и сам обновляется.',
  empty: 'Здесь появятся компьютеры, которые вы подключите к табелю.',
  connectIntro: 'Выполните команду в терминале компьютера, который хотите подключить.',
  keyHidden: 'Ключ в команде скрыт. «Скопировать» копирует команду целиком.',
  serviceMacos: 'Коннектор встанет службой и будет запускаться при входе в систему.',
  serviceLinux: 'Коннектор встанет службой пользователя и будет запускаться сам.',
  waiting: 'Ждём компьютер. Когда установка закончится, здесь появится «в сети».',
  connected: 'Коннектор встал службой и будет запускаться сам.',
  expired: 'Срок ключа истёк. Чтобы подключить компьютер, получите новый ключ.',
  copyFailed: 'Не получилось скопировать. Выделите команду и скопируйте её вручную.',
  disableFailed: 'Компьютер не отключён',
  /** FR-004 (решение 18.10.2026): коннектор отключённого компьютера удаляет себя сам. */
  removeManual:
    'Когда компьютер выйдет в сеть, коннектор удалит себя сам. Если компьютер больше не подключится, выполните команду в его терминале.',
  autoRefresh: 'Пока страница открыта, состояние обновляется само каждые 3 секунды.',
  agentsUnknown: 'Когда компьютер снова будет в сети, состояние агентов станет известно.',
  loggedOut: 'Войдите в агента в терминале этого компьютера. Табель вход не делает.',
  /** Codex — только ключом API (FR-060). Команда сама спрашивает ключ, ввод не виден. */
  codexLogin:
    'Codex работает в табеле с ключом API OpenAI. Ключ создайте на сайте platform.openai.com, в разделе ключей API. Выполните команду в терминале этого компьютера: она спросит ключ. Вставьте ключ и нажмите Enter. Ключ не будет виден на экране и не попадёт в историю терминала.',
  /** pi — любой поставщик, кроме подписки Claude (FR-060, решение 18.10.2026). */
  piLogin:
    'pi работает в табеле с любым поставщиком, кроме подписки Claude. Подойдёт ключ API (DeepSeek, OpenRouter, OpenAI, Anthropic и другие) или вход через ChatGPT. Войдите в pi в терминале этого компьютера и выберите поставщика.',
  piClaude:
    'Вы вошли в pi по подписке Claude. Anthropic разрешает подписку Claude только в Claude Code, поэтому табель не запускает её через pi. Для Claude выберите агента Claude. Для pi войдите в другого поставщика: с ключом API или через ChatGPT.',
  piClaudeReady: 'Модели Claude по подписке в списке pi не показываются. Для Claude выберите агента Claude.',
  notLinked: 'Агент ещё не подключён к табелю. Выполните в терминале:',
  /** pi читает настройки папки входа только после доверия к ней (стенд 18.10.2026). */
  piNotLinked:
    'pi ещё не подключён к табелю. Выполните команду в терминале. pi спросит, доверять ли этой папке: ответьте «да». Затем введите обе строки по очереди: прежний вход pi в табель удалится, и откроется браузер. Войдите в табель под своей учётной записью.',
  /** FR-060: Codex на подписке ChatGPT — только после одобрения заявки «Sign in with ChatGPT». */
  codexSubscription:
    'Codex на подписке ChatGPT пока недоступен во встроенном чате. Войдите ключом API или работайте в терминале.',
  unknownOnline: 'Состояние агента ещё не проверено. Табель проверит его через несколько секунд.',
  unknownOffline: 'Когда компьютер снова будет в сети, состояние агента станет известно.',
} as const;

const two = (n: number) => String(n).padStart(2, '0');

/** Одно число из 1, 21, 31…: «1 разговор». */
const isOne = (n: number) => n % 10 === 1 && n % 100 !== 11;

/** «1 разговор», «2 разговора», «5 разговоров». */
export function threadsText(n: number): string {
  const d = n % 10;
  const dd = n % 100;
  const few = d >= 2 && d <= 4 && (dd < 12 || dd > 14);
  return `${n} ${isOne(n) ? 'разговор' : few ? 'разговора' : 'разговоров'}`;
}

/**
 * Тексты 006 с подстановкой. `name` — имя компьютера или агента, `when` — время по времени объекта, `prog` — программа
 * агента (`PROGRAM`), `ver` — версия.
 */
export const pcSay = {
  /** Окно: «Ключ действует ещё 14:59 и только один раз». */
  keyLeft: (ms: number) => {
    const s = Math.max(0, Math.ceil(ms / 1000));
    return `Ключ действует ещё ${Math.floor(s / 60)}:${two(s % 60)} и только один раз.`;
  },
  connected: (name: string) => `Компьютер ${name} в сети.`,
  /**
   * Строка компьютера: «macOS · коннектор 0.3.1 · агенты: Claude, pi». `agents = null` — состояние агентов ещё не
   * пришло (компьютер только что подключился): про агентов ничего не пишем.
   */
  pcLine: (os: string, version: string | null, agents: readonly string[] | null) =>
    [
      os,
      version ? `коннектор ${version}` : null,
      agents === null ? null : agents.length > 0 ? `агенты: ${agents.join(', ')}` : PC_LABEL.noAgents,
    ]
      .filter(Boolean)
      .join(' · '),
  pcDisabledLine: (os: string, when: string) => `${os} · отключён ${when}`,
  offlineSince: (when: string) => `не в сети с ${when}`,
  showAgentsOf: (name: string) => `Показать агентов на ${name}`,
  disableName: (name: string) => `Отключить компьютер ${name}`,
  disableTitle: (name: string) => `Отключить компьютер ${name}?`,
  /** Последствия отключения числами (дизайн-система, ConfirmDialog): разговоры перестанут работать, переписка останется. */
  disableText: (threads: number) =>
    [
      'Коннектор остановится и удалит себя с этого компьютера: службу, свою папку и переписку агентов на нём.',
      'Если компьютер сейчас не в сети, коннектор удалит себя после его выхода в сеть.',
      threads > 0
        ? `${threadsText(threads)} на этом компьютере ${isOne(threads) ? 'перестанет' : 'перестанут'} работать, переписка останется.`
        : null,
      'Чтобы подключить компьютер снова, нужен новый ключ.',
    ]
      .filter(Boolean)
      .join(' '),
  disabled: (name: string) => `Компьютер ${name} отключён`,
  alreadyDisabled: (name: string) => `Компьютер ${name} уже отключён`,
  agentsOn: (name: string) => `Агенты на ${name}`,
  checkedAt: (hms: string) => `Проверено в ${hms}.`,
  pcOfflineSince: (when: string) => `Компьютер не в сети с ${when}.`,
  check: (name: string) => `Проверить ${name}`,
  install: (name: string) => `Установить ${name}`,
  update: (name: string) => `Обновить ${name}`,
  copyCommand: (name: string) => `Скопировать команду для ${name}`,
  /** Строка-переключатель свёрнутых отключённых компьютеров (приёмка 18.10.2026). */
  disabledComputers: (n: number) => `Отключённые компьютеры (${n})`,
  /** Строка версии и входа: «Claude Code 2.1.302 · вход: подписка Claude · подключён к табелю». */
  versionLine: (prog: string, ver: string | null, login: string | null, linked?: boolean) =>
    [
      ver ? `${prog} ${ver}` : prog,
      login ? `вход: ${login}` : null,
      linked === undefined ? null : linked ? 'подключён к табелю' : 'не подключён к табелю',
    ]
      .filter(Boolean)
      .join(' · '),
  /** Нужна новая версия: у Codex — точная версия своего экземпляра, у остальных — нижняя граница. */
  outdated: (prog: string, ver: string | null, min: string, exact: boolean) =>
    `${ver ? `${prog} ${ver}` : prog}. Нужна ${min}${exact ? '' : ' или новее'}.`,
  notInstalled: (prog: string) => `${prog} не найден на этом компьютере. Коннектор поставит его сам.`,
  installing: (prog: string) => `Коннектор ставит ${prog} своей командой. Одновременно идёт одна установка.`,
  /** Команда входа в несколько строк (вход pi): первая — в терминале, остальные — внутри агента. */
  inside: (name: string) => `Внутри ${name} введите:`,
  /** Последняя установка не удалась; `action` — подпись кнопки «Установить …» или «Обновить …». */
  installFailed: (action: string) =>
    `Установка не удалась. Посмотрите вывод ниже и нажмите «${action}» ещё раз.`,
};
